import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import toast from "react-hot-toast";
import {
  showCustomOrder,
  isErpApiSuccess,
  getErpBranchDetails,
  getTenantBrandImageSrcWithFallback,
} from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { capitalizeWords } from "../utils/capitalizeWords";
import { getTenantNameUpper } from "../utils/tenantName";

/** Split branch address into 2 lines: street/area on line 1, rest on line 2. */
function formatBranchAddressLines(address) {
  const raw = String(address || "").trim();
  if (!raw) return ["-"];
  if (raw.includes("\n")) {
    return raw
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw];
  if (parts.length === 2) return [`${parts[0]},`, parts[1]];
  // e.g. "34/2 Swaminarayan Nagar, Part-2," / "Nikol Road, Ahmedabad, Gujarat 382350"
  return [`${parts.slice(0, 2).join(", ")},`, parts.slice(2).join(", ")];
}

/**
 * Bill carat/purity label (right of Item Name), matching sales slip rules:
 * - Gold: 24c / 22c / 18c (also 14c / 9c when present)
 * - Silver 92.5 / 925 → "925"
 * - Other silver → ""
 */
function formatCustomOrderItemCaratLabel(item = {}) {
  const metal = String(item.metal_type ?? item.metal ?? "").toLowerCase();
  const nameHint = String(
    item.product_group_name ||
      item.product_group ||
      item.item_category_name ||
      item.itemCategoryName ||
      item.name ||
      "",
  ).toLowerCase();

  const isGold =
    metal.includes("gold") ||
    /\bgold\b|\bcarat\b|\bct\b|\bk\b/.test(nameHint);
  const isSilver =
    metal.includes("silver") ||
    /\bsilver\b|\b925\b|\b999\b|\b92\.5\b/.test(nameHint);

  let value = parseFloat(
    item.carat ?? item.received_purity ?? item.purity ?? item.metal_purity ?? "",
  );

  if (!Number.isFinite(value) || value <= 0) {
    const goldMatch = nameHint.match(/\b(24|22|18|14|9)\s*(?:carat|ct|k|c)\b/);
    if (goldMatch) {
      value = Number(goldMatch[1]);
    } else {
      const silverMatch = nameHint.match(/\b(92\.5|925|999)\b/);
      if (silverMatch) {
        value =
          silverMatch[1] === "92.5" ? 925 : Number(silverMatch[1]);
      }
    }
  }

  if (!Number.isFinite(value) || value <= 0) return "";

  // Normalize 92.5 → 925 for silver display checks
  if (Math.abs(value - 92.5) < 0.01) value = 925;

  if (isSilver && !isGold) {
    return value === 925 ? "925" : "";
  }

  // Gold (or karat-scale values)
  if (isGold || value <= 24) {
    const carat = Math.round(value);
    if (carat > 0 && carat <= 24) return `${carat}c`;
  }

  // Ambiguous silver-only purity without metal hint
  if (value === 925) return "925";

  return "";
}

// Register Gujarati font for better text rendering
Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

// A5 landscape — 8mm gap between page edge and content border (all sides)
const PAGE_MARGIN = 22.68; // 8mm in pt (8 * 72 / 25.4)
const A5_WIDTH = 595.28;
const A5_HEIGHT = 419.53;
const CONTENT_HEIGHT = A5_HEIGHT - PAGE_MARGIN * 2;

// Create styles - Use Helvetica as default font
const styles = StyleSheet.create({
  page: {
    width: A5_WIDTH,
    height: A5_HEIGHT,
    padding: PAGE_MARGIN,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
    flexDirection: "column",
  },

  container: {
    height: CONTENT_HEIGHT,
    border: "1pt solid #8c1d1a",
    borderRadius: 6,
    padding: 0,
    flexDirection: "column",
  },

  topHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 2,
    marginBottom: 2,
    paddingTop: 2,
    marginTop: 0,
    borderBottom: "1pt solid #8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 10,
    paddingRight: 10,
    flexShrink: 0,
  },

  topHeaderText: {
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
    color: "#8c1d1a",
  },

  topHeaderLeft: {
    width: "34%",
    alignItems: "flex-start",
  },

  topHeaderCenter: {
    width: "32%",
    alignItems: "center",
  },

  topHeaderRight: {
    width: "34%",
    alignItems: "flex-end",
  },

  header: {
    position: "relative",
    paddingTop: 4,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottom: "1pt solid #8c1d1a",
    minHeight: 44,
    flexShrink: 0,
  },

  logoCol: {
    position: "absolute",
    left: 8,
    top: 6,
    width: 36,
    height: 36,
    zIndex: 2,
  },

  centerBrandBlock: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 70,
  },

  companyTitle: {
    fontFamily: "Times-Bold",
    fontSize: 22,
    color: "#7d0000",
    letterSpacing: 0.4,
    textAlign: "center",
  },

  companyAddress: {
    fontSize: 7,
    marginTop: 2,
    textAlign: "center",
    width: "100%",
  },

  mobileCol: {
    position: "absolute",
    right: 8,
    top: 6,
    zIndex: 2,
  },

  mobileText: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "right",
    fontFamily: "NotoSansGujaratiUI",
  },

  titleSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1pt solid #8c1d1a",
    paddingBottom: 0,
    marginBottom: 0,
    backgroundColor: "#8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
    flexShrink: 0,
  },

  titleText: {
    fontSize: 13,
    fontWeight: "medium",
    color: "white",
  },

  sellerSection: {
    marginBottom: 10,
  },

  sellerText: {
    fontSize: 8,
    marginBottom: 1,
    lineHeight: 1.4,
  },

  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 1,
  },

  // Fixed widths match longest label in each group (keeps `:` aligned, no extra gap)
  infoLabelLeft: {
    width: 26,
    fontSize: 8,
    lineHeight: 1.4,
  },

  infoLabelOrderNo: {
    width: 33,
    fontSize: 8,
    lineHeight: 1.4,
  },

  infoLabelCenter: {
    width: 53,
    fontSize: 8,
    lineHeight: 1.4,
  },

  infoColon: {
    width: 4,
    fontSize: 8,
    lineHeight: 1.4,
  },

  infoValue: {
    fontSize: 8,
    lineHeight: 1.4,
    marginLeft: 1,
  },

  amountHighlight: {
    fontSize: 11,
    fontWeight: "bold",
    marginVertical: 4,
  },

  table: {
    border: "1pt solid #8c1d1a",
    marginTop: 6,
    marginBottom: 4,
    marginLeft: 10,
    marginRight: 10,
    borderRadius: 6,
    flex: 1,
    flexDirection: "column",
    overflow: "hidden",
  },

  tableBodyFiller: {
    flex: 1,
    minHeight: 8,
    flexDirection: "row",
    alignItems: "stretch",
  },

  fillerCol: {
    borderBottom: 0,
    minHeight: "100%",
  },

  footerBlock: {
    flexShrink: 0,
    paddingLeft: 10,
    paddingRight: 10,
    paddingBottom: 4,
  },

  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #8c1d1a",
    fontSize: 8,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    fontWeight: "bold",
    backgroundColor: "#f5e9e9",
  },

  tableRow: {
    flexDirection: "row",
    fontSize: 8,
  },

  tableCol1: {
    width: "8%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol2: {
    width: "10%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    fontSize: 6,
  },
  tableCol3: {
    width: "20%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    fontSize: 6,
  },
  itemNameCell: {
    width: "20%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    fontSize: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  itemNameText: {
    flex: 1,
    paddingRight: 4,
    fontSize: 6,
  },
  itemCaratText: {
    fontSize: 6,
    fontWeight: "bold",
    textAlign: "right",
  },
  tableCol4: {
    width: "8%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol5: {
    width: "6%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol6: {
    width: "6%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol7: {
    width: "8%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol8: {
    width: "8%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol9: {
    width: "8%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol10: {
    width: "8%",
    padding: 2,
    borderRight: "1pt solid #8c1d1a",
    textAlign: "center",
    fontSize: 6,
  },
  tableCol11: { width: "8%", padding: 2, textAlign: "center", fontSize: 6 },

  noteRow: {
    flexDirection: "row",
    borderTop: "1pt solid #8c1d1a",
  },

  totalRow: {
    flexDirection: "row",
    borderTop: "1pt solid #8c1d1a",
    borderBottom: "1pt solid #8c1d1a",
    backgroundColor: "#f5e9e9",
  },

  noteCol1: {
    width: "40%",
    padding: 6,
    borderRight: "1pt solid #8c1d1a",
    fontWeight: "bold",
    fontSize: 9,
  },

  noteCol2: {
    width: "60%",
    padding: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 9,
  },

  declarationText: {
    fontSize: 7,
    lineHeight: 1.5,
    textAlign: "justify",
  },

  footerSection: {
    marginTop: 6,
  },

  footerRow: {
    flexDirection: "row",
    marginBottom: 3,
    fontSize: 10,
  },

  footerLabel: {
    fontWeight: "bold",
    width: 50,
  },

  signatureLabel: {
    fontSize: 7,
    marginBottom: 7,
  },

  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 20,
  },

  signatureBox: {
    borderBottom: "1pt solid #8c1d1a",
    width: 280,
    height: 40,
    marginBottom: 5,
  },

  signatureColumn: {
    alignItems: "center",
    width: 280,
  },
});

// Helper component for displaying label-value rows
const Row = ({ label, value, bold = false, showBorder = false }) => (
  <View
    style={{
      flexDirection: "row",
      justifyContent: "space-between",
      paddingVertical: 2,
      borderBottom: showBorder ? "1pt solid #8c1d1a" : "none",
      marginLeft: -5,
      marginRight: -5,
      paddingLeft: 5,
      paddingRight: 5,
    }}
  >
    <Text
      style={{
        fontSize: 7,
        fontWeight: bold ? "bold" : "normal",
      }}
    >
      {label}
    </Text>
    <Text
      style={{
        fontSize: 7,
        fontWeight: bold ? "bold" : "normal",
        textAlign: "right",
      }}
    >
      {typeof value === "number" ? value.toFixed(2) : value}
    </Text>
  </View>
);

// Helper function to convert numbers to words
const numberToWords = (num) => {
  const a = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const b = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  const convertLessThanOneThousand = (n) => {
    let res = "";
    if (n % 100 < 20) {
      res = a[n % 100];
      n = Math.floor(n / 100);
    } else {
      res = b[Math.floor((n % 100) / 10)] + " " + a[n % 10];
      n = Math.floor(n / 100);
    }
    if (n > 0) {
      res = a[n] + " hundred " + res;
    }
    return res.trim();
  };

  // Handle decimal part for paise
  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  if (integerPart === 0 && decimalPart === 0) return "Zero Only";

  let words = [];

  // Process integer part
  if (integerPart > 0) {
    let n = integerPart;

    // Crore
    const crore = Math.floor(n / 10000000);
    if (crore > 0) {
      words.push(convertLessThanOneThousand(crore) + " crore");
      n = n % 10000000;
    }

    // Lakh
    const lakh = Math.floor(n / 100000);
    if (lakh > 0) {
      words.push(convertLessThanOneThousand(lakh) + " lakh");
      n = n % 100000;
    }

    // Thousand
    const thousand = Math.floor(n / 1000);
    if (thousand > 0) {
      words.push(convertLessThanOneThousand(thousand) + " thousand");
      n = n % 1000;
    }

    // Hundred and below
    if (n > 0) {
      words.push(convertLessThanOneThousand(n));
    }
  }

  let result = words.join(" ").trim();

  // Add paise if decimal part exists
  if (decimalPart > 0) {
    const paiseWords = convertLessThanOneThousand(decimalPart);
    result += " And " + paiseWords + " Paise";
  }

  result += " Only";

  // Capitalize first letter of each word
  return result
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

// PDF Document Component
const CustomOrderPDFDocument = ({ orderData }) => {
  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyMobile = branchDetails.mobile || branchDetails.phone || "-";
  const companyNameUpper = getTenantNameUpper();
  const addressLines = formatBranchAddressLines(branchDetails.address);
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() ||
    addressLines.join(", ").replace(/\s+/g, " ").trim() ||
    "-";

  const formatDate = (dateString) => {
    if (!dateString) {
      // Fallback to current date if no date is provided
      const today = new Date();
      return today.toLocaleDateString("en-GB");
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (e) {
      console.log("Date parsing error:", e);
      // Fallback to current date if parsing fails
      const today = new Date();
      return today.toLocaleDateString("en-GB");
    }
  };

  const calculateTotals = () => {
    if (!orderData?.custom_order_entry)
      return {
        totalPcs: 0,
        totalGwt: 0,
        totalLwt: 0,
        totalNwt: 0,
        totalAmount: 0,
        lbrAmt: 0,
        othAmt: 0,
      };

    const totals = orderData.custom_order_entry.reduce(
      (acc, item) => {
        const pcs = Number(item.pcs) || 0;
        const gwt = Number(item.gwt || item.current_weight) || 0;
        const lwt = Number(item.lwt) || 0;
        const nwt = Number(item.nwt || item.exp_weight) || 0;
        const totalAmt = Number(item.totalamt || item.total_amount) || 0;
        const lbrRate = Number(item.lbr_rate || item.rate) || 0;
        const othAmt = Number(item.oth_amt || 0);

        acc.totalPcs += pcs;
        acc.totalGwt += gwt;
        acc.totalLwt += lwt;
        acc.totalNwt += nwt;
        acc.totalAmount += totalAmt;
        acc.lbrAmt += lbrRate;
        acc.othAmt += othAmt;

        return acc;
      },
      {
        totalPcs: 0,
        totalGwt: 0,
        totalLwt: 0,
        totalNwt: 0,
        totalAmount: 0,
        lbrAmt: 0,
        othAmt: 0,
      },
    );

    return totals;
  };

  const totals = calculateTotals();

  const parseAmount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  // GST and round-off are stored on the order. round_off is the signed
  // nearest-rupee adjustment (negative removes paise).
  const gstAmount = parseAmount(
    orderData?.tax_amount ?? orderData?.gst_amount ?? orderData?.gst_amt,
  );
  const roundOff = parseAmount(orderData?.round_off ?? orderData?.roundOff);
  const advanceAmount = (orderData?.payments || []).reduce(
    (sum, payment) =>
      sum +
      parseAmount(
        payment?.amount ??
          payment?.aavak_amt ??
          payment?.total_amount ??
          payment?.amt,
      ),
    0,
  );
  const amountWithGst = totals.totalAmount + gstAmount;
  const payableAmount =
    Math.round((amountWithGst + roundOff) * 100) / 100;
  const outstandingAmount =
    Math.round((payableAmount - advanceAmount) * 100) / 100;

  return (
    <Document>
      <Page size="A5" orientation="landscape" style={styles.page} wrap={false}>
        <View style={styles.container}>
          <View style={styles.topHeader}>
            <View style={styles.topHeaderLeft}>
              <Text style={styles.topHeaderText}>
                || શ્રી પદમાવતી માતાય નમ: ||
              </Text>
            </View>
            <View style={styles.topHeaderCenter}>
              <Text style={styles.topHeaderText}>|| શ્રી ગણેશાય નમ: ||</Text>
            </View>
            <View style={styles.topHeaderRight}>
              <Text style={styles.topHeaderText}>
                || શ્રી બુટભવાની માતાય નમ: ||
              </Text>
            </View>
          </View>

          <View style={styles.header}>
            <View style={styles.mobileCol}>
              <Text style={styles.mobileText} wrap={false}>
                MO.: {companyMobile}
              </Text>
            </View>
            <View style={styles.logoCol}>
              <Image
                src={getTenantBrandImageSrcWithFallback("sidebar_icon")}
                style={{ height: 32, width: 32 }}
              />
            </View>
            <View style={styles.centerBrandBlock}>
              <Text style={styles.companyTitle} wrap={false}>
                {companyNameUpper}
              </Text>
              <Text style={styles.companyAddress} wrap={false}>
                {addressSingleLine}
              </Text>
            </View>
          </View>

          <View style={styles.titleSection}>
            <View style={{ flex: 1, alignItems: "center" }}>
              <Text style={styles.titleText}>Custom Order</Text>
            </View>
          </View>

          <View
            style={{
              marginTop: 4,
              paddingLeft: 10,
              paddingRight: 10,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <View style={{ width: "38%", alignItems: "flex-start" }}>
              <View style={[styles.infoRow, { alignSelf: "flex-start" }]}>
                <Text style={styles.infoLabelLeft}>Name</Text>
                <Text style={styles.infoColon}>:</Text>
                <Text style={styles.infoValue} wrap={false}>
                  {capitalizeWords(orderData?.name) || "N/A"}
                </Text>
              </View>
              <View style={[styles.infoRow, { alignSelf: "flex-start" }]}>
                <Text style={styles.infoLabelLeft}>Mobile</Text>
                <Text style={styles.infoColon}>:</Text>
                <Text style={styles.infoValue} wrap={false}>
                  {orderData?.mobile || "N/A"}
                </Text>
              </View>
            </View>
            <View style={{ width: "34%", alignItems: "center" }}>
              <View style={{ alignSelf: "center", alignItems: "flex-start" }}>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabelCenter}>Order Date</Text>
                  <Text style={styles.infoColon}>:</Text>
                  <Text style={styles.infoValue} wrap={false}>
                    {formatDate(
                      orderData?.created_at ||
                        orderData?.doc_date ||
                        orderData?.document_date ||
                        orderData?.order_date ||
                        orderData?.date,
                    )}
                  </Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabelCenter}>Delivery Date</Text>
                  <Text style={styles.infoColon}>:</Text>
                  <Text style={styles.infoValue} wrap={false}>
                    {orderData?.exp_delivery ||
                    orderData?.expected_delivery ||
                    orderData?.exp_date ||
                    orderData?.delivery_date
                      ? formatDate(
                          orderData?.exp_delivery ||
                            orderData?.expected_delivery ||
                            orderData?.exp_date ||
                            orderData?.delivery_date,
                        )
                      : "N/A"}
                  </Text>
                </View>
              </View>
            </View>
            <View
              style={{ width: "28%", alignItems: "flex-end", paddingRight: 0 }}
            >
              <View style={[styles.infoRow, { alignSelf: "flex-end" }]}>
                <Text style={styles.infoLabelOrderNo}>Order No</Text>
                <Text style={styles.infoColon}>:</Text>
                <Text style={styles.infoValue} wrap={false}>
                  {orderData?.co_number || "N/A"}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.table}>
            {/* HEADER */}
            <View style={styles.tableHeader}>
              <Text style={styles.tableCol1}>No.</Text>
              <Text style={styles.tableCol2}>Tag No</Text>
              <Text style={styles.tableCol3}>Item Name</Text>
              <Text style={styles.tableCol4}>Design</Text>
              <Text style={styles.tableCol5}>Size</Text>
              <Text style={styles.tableCol6}>Pcs</Text>
              <Text style={styles.tableCol7}>Gross Wt.</Text>
              <Text style={styles.tableCol8}>Net Wt.</Text>
              <Text style={styles.tableCol9}>ItRate</Text>
              <Text style={styles.tableCol10}>OthAmt</Text>
              <Text style={styles.tableCol11}>Lbr Rate</Text>
            </View>

            {/* DATA */}
            {orderData?.custom_order_entry?.map((item, index) => {
              const pcs = Number(item.pcs) || 0;
              const gwt = Number(item.gwt || item.current_weight) || 0;
              const nwt = Number(item.nwt || item.exp_weight) || 0;
              const itr = Number(item.itrate) || 0;
              const othAmt = Number(item.oth_amt || 0);
              const lbrRate = Number(item.lbr_rate || item.rate) || 0;
              const itemName =
                item.item_category_name || item.itemCategoryName || "";
              const caratLabel = formatCustomOrderItemCaratLabel(item);

              return (
                <View style={styles.tableRow} key={index}>
                  <Text style={styles.tableCol1}>{index + 1}</Text>
                  <Text style={styles.tableCol2}>{item.tag_no || ""}</Text>
                  <View style={styles.itemNameCell}>
                    <Text style={styles.itemNameText}>{itemName}</Text>
                    {caratLabel ? (
                      <Text style={styles.itemCaratText}>{caratLabel}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.tableCol4}>{item.design || ""}</Text>
                  <Text style={styles.tableCol5}>{item.size || ""}</Text>
                  <Text style={styles.tableCol6}>{pcs}</Text>
                  <Text style={styles.tableCol7}>{gwt.toFixed(3)}</Text>
                  <Text style={styles.tableCol8}>{nwt.toFixed(3)}</Text>
                  <Text style={styles.tableCol9}>{itr.toFixed(2)}</Text>
                  <Text style={styles.tableCol10}>{othAmt.toFixed(2)}</Text>
                  <Text style={styles.tableCol11}>{lbrRate.toFixed(2)}</Text>
                </View>
              );
            })}

            {/* Grow empty area so totals / note sit near the bottom;
                column borders continue through the blank space */}
            <View style={styles.tableBodyFiller}>
              <View style={[styles.tableCol1, styles.fillerCol]} />
              <View style={[styles.tableCol2, styles.fillerCol]} />
              <View style={[styles.tableCol3, styles.fillerCol]} />
              <View style={[styles.tableCol4, styles.fillerCol]} />
              <View style={[styles.tableCol5, styles.fillerCol]} />
              <View style={[styles.tableCol6, styles.fillerCol]} />
              <View style={[styles.tableCol7, styles.fillerCol]} />
              <View style={[styles.tableCol8, styles.fillerCol]} />
              <View style={[styles.tableCol9, styles.fillerCol]} />
              <View style={[styles.tableCol10, styles.fillerCol]} />
              <View style={[styles.tableCol11, styles.fillerCol]} />
            </View>

            {/* TOTAL */}
            <View style={styles.totalRow}>
              <Text style={[styles.tableCol1, { fontWeight: "bold" }]}>
                Total
              </Text>
              <Text style={styles.tableCol2}></Text>
              <Text style={styles.tableCol3}></Text>
              <Text style={styles.tableCol4}></Text>
              <Text style={styles.tableCol5}></Text>
              <Text style={[styles.tableCol6, { fontWeight: "bold" }]}>
                {totals.totalPcs}
              </Text>
              <Text style={[styles.tableCol7, { fontWeight: "bold" }]}>
                {totals.totalGwt.toFixed(3)}
              </Text>
              <Text style={[styles.tableCol8, { fontWeight: "bold" }]}>
                {totals.totalNwt.toFixed(3)}
              </Text>
              <Text style={styles.tableCol9}></Text>
              <Text style={styles.tableCol10}></Text>
              <Text style={styles.tableCol11}></Text>
            </View>

            {/* EMPTY + SUMMARY SAME TABLE */}
            <View style={styles.tableRow}>
              {/* PAYMENT SUMMARY */}
              <View
                style={{
                  width: "62%",
                  borderRight: "none",
                  borderBottomLeftRadius: 6,
                  backgroundColor: "#fafafa",
                }}
              >
                {/* NOTE SECTION */}
                <View style={{ padding: 2, borderBottom: "1pt solid #8c1d1a" }}>
                  <Text style={{ fontSize: 7, fontWeight: "bold" }}>Note:</Text>
                  <Text style={{ fontSize: 7 }}>{orderData?.note || ""}</Text>
                </View>

                {/* AMOUNT IN WORDS WITH SEPARATORS */}
                {orderData?.payments?.length > 0 && (
                  <View>
                    <Text
                      style={{
                        fontSize: 7,
                        fontStyle: "italic",
                        textAlign: "start",
                        padding: 2,
                      }}
                    >
                      Rupees{" "}
                      {numberToWords(Math.round(payableAmount))}
                    </Text>
                  </View>
                )}

                {/* PAYMENT TABLE */}
                {orderData?.payments?.length > 0 ? (
                  <View style={{ borderBottom: "1pt solid #8c1d1a" }}>
                    {/* PAYMENT TABLE HEADER */}
                    <View
                      style={{
                        flexDirection: "row",
                        fontSize: 6,
                        fontWeight: "bold",
                        backgroundColor: "#f5e9e9",
                        borderTop: "1pt solid #8c1d1a",
                        borderBottom: "1pt solid #8c1d1a",
                      }}
                    >
                      <Text
                        style={{
                          width: "33%",
                          padding: 2,
                          borderRight: "1pt solid #8c1d1a",
                          textAlign: "center",
                        }}
                      >
                        Bank Name
                      </Text>
                      <Text
                        style={{
                          width: "34%",
                          padding: 2,
                          borderRight: "1pt solid #8c1d1a",
                          textAlign: "center",
                        }}
                      >
                        Payment Mode
                      </Text>
                      <Text
                        style={{
                          width: "33%",
                          padding: 2,
                          textAlign: "center",
                        }}
                      >
                        Amount
                      </Text>
                    </View>

                    {/* PAYMENT TABLE DATA */}
                    {orderData.payments.map((payment, index) => (
                      <View
                        key={index}
                        style={{
                          flexDirection: "row",
                          fontSize: 6,
                          borderBottom: "0.10pt solid #8c1d1a",
                        }}
                      >
                        <Text
                          style={{
                            width: "33%",
                            padding: 3,
                            borderRight: "1pt solid #8c1d1a",
                            textAlign: "center",
                          }}
                        >
                          {payment.bank_name || "N/A"}
                        </Text>
                        <Text
                          style={{
                            width: "34%",
                            padding: 3,
                            borderRight: "1pt solid #8c1d1a",
                            textAlign: "center",
                          }}
                        >
                          {payment.payment_mode || "N/A"}
                        </Text>
                        <Text
                          style={{
                            width: "33%",
                            padding: 3,
                            textAlign: "center",
                            fontWeight: "bold",
                          }}
                        >
                          {Number(payment.amount || 0).toFixed(2)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={{ padding: 2, paddingTop: 6, paddingBottom: 8 }}>
                    <Text
                      style={{
                        fontSize: 7,
                        color: "#666666",
                        textAlign: "left",
                      }}
                    >
                      No payments recorded
                    </Text>
                  </View>
                )}
              </View>

              {/* RIGHT SUMMARY */}
              <View
                style={{
                  width: "38%",
                  borderBottomRightRadius: 8,
                  paddingLeft: 5,
                  paddingRight: 5,
                  backgroundColor: "#fafafa",
                  borderLeft: "1pt solid #8c1d1a",
                }}
              >
                <Row
                  label="Metal Rs."
                  value={totals.totalAmount}
                  showBorder={true}
                />

                <Row
                  label="Approx Labour Rs."
                  value={totals.lbrAmt}
                  showBorder={true}
                />

                <Row
                  label="Approx Extra Rs."
                  value={totals.othAmt}
                  showBorder={true}
                />

                <Row
                  label="Approx GST Rs."
                  value={gstAmount}
                  showBorder={true}
                />

                <Row
                  label="Approx Total Rs."
                  value={amountWithGst}
                  bold
                  showBorder={true}
                />

                <Row
                  label="Advance Rs."
                  value={advanceAmount}
                  showBorder={true}
                />

                <Row
                  label="Approx Outstanding"
                  value={outstandingAmount}
                  showBorder={false}
                />
              </View>
            </View>
          </View>

          <View style={styles.footerBlock}>
            <Text style={styles.declarationText}>
              This jewellery has been ordered and will be manufactured as per
              the specifications. The customer agrees to the terms and
              conditions mentioned in the order. Any changes to the design or
              specifications will require mutual consent.
            </Text>

            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                paddingHorizontal: 30,
              }}
            >
              <View style={{ alignItems: "center" }}>
                <View
                  style={{
                    borderBottom: "1pt solid #8c1d1a",
                    width: 180,
                    height: 28,
                    marginBottom: 4,
                  }}
                />
                <Text style={{ fontSize: 7, marginBottom: 4 }}>
                  Customer Signature
                </Text>
              </View>
              <View style={{ alignItems: "center" }}>
                <View
                  style={{
                    borderBottom: "1pt solid #8c1d1a",
                    width: 180,
                    height: 28,
                    marginBottom: 4,
                  }}
                />
                <Text style={{ fontSize: 7, marginBottom: 4 }}>
                  Authorized Signature
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Normalize function similar to EditCustomOrderEntry.jsx
const normalizeCustomOrderForEdit = (raw) => {
  const base = raw || {};
  const normalizedItems = Array.isArray(base.custom_order_entry)
    ? base.custom_order_entry
    : Array.isArray(base.custom_order)
      ? base.custom_order
      : Array.isArray(base.entries)
        ? base.entries
        : Array.isArray(base.items)
          ? base.items
          : [];

  return {
    ...base,
    custom_order_entry: normalizedItems,
    user_id: base.user_id ?? base.customer_id ?? "",
    name: base.name ?? base.customer_name ?? base.user_name ?? "",
    mobile: base.mobile ?? base.customer_mobile ?? base.user_mobile ?? "",
    exp_delivery: base.exp_delivery ?? base.expected_delivery ?? "",
    final_amt: base.final_amt ?? base.final_amount ?? base.grand_total ?? 0,
    total: base.total ?? base.grand_total ?? base.final_amount ?? 0,
    note: base.note ?? base.notes ?? "",
    payments: Array.isArray(base.payments) ? base.payments : [],
    co_number:
      base.co_number ??
      base.order_number ??
      base.doc_number ??
      base.tag_no ??
      "",
  };
};

function CustomOrderPdfModal({ show, onHide, orderId }) {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchOrderData = async () => {
      if (!show || !orderId) return;

      setLoading(true);
      setOrderData(null);
      try {
        const response = await showCustomOrder({ id: orderId });

        if (
          (isErpApiSuccess(response) || response?.message === "radha") &&
          response?.data
        ) {
          const rawCustomOrderData =
            response.data.custom_order_details ||
            response.data.data?.custom_order_details ||
            response.data;
          const customOrderData =
            normalizeCustomOrderForEdit(rawCustomOrderData);

          if (customOrderData) {
            setOrderData(customOrderData);
          } else {
            toast.error("Failed to process custom order data");
          }
        } else {
          toast.error(response?.message || "Failed to fetch custom order data");
        }
      } catch (error) {
        console.error("Error fetching custom order data:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error.message ||
            "Failed to fetch custom order data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [show, orderId]);

  // Memoize PDF content
  const pdfContent = useMemo(() => {
    if (!orderData) return null;

    return (
      <PDFViewer width="100%" height="100%">
        <CustomOrderPDFDocument orderData={orderData} />
      </PDFViewer>
    );
  }, [orderData]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="pdf-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Custom Order PDF Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading PDF data...</span>
            </div>
          </div>
        ) : pdfContent ? (
          pdfContent
        ) : (
          <div className="text-center p-4">
            <div className="mb-3">
              <i
                className="bi bi-file-earmark-pdf"
                style={{ fontSize: "3rem", color: "#6c757d" }}
              ></i>
            </div>
            <h5 className="text-muted">No PDF Data Available</h5>
            <p className="text-muted">
              {orderData
                ? "No items found for this custom order"
                : "Unable to load custom order data"}
            </p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default CustomOrderPdfModal;
